import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { listConnections } from '../../api/connections';
import { listDevices, type DeviceListItem } from '../../api/devices';
import { billingSummary } from '../../components/billingView';

interface Counts {
  connections: number;
  devices: number;
  connectionsNeedingAttention: number;
  devicesNeedingAttention: number;
}

export default function OverviewPage() {
  const { user } = useAuth();
  const [counts, setCounts] = useState<Counts | null>(null);
  const [recentDevices, setRecentDevices] = useState<DeviceListItem[]>([]);

  useEffect(() => {
    let active = true;
    Promise.allSettled([listConnections(), listDevices()]).then(([connections, devices]) => {
      if (!active) return;
      setCounts({
        connections: connections.status === 'fulfilled' ? connections.value.length : -1,
        devices: devices.status === 'fulfilled' ? devices.value.length : -1,
        connectionsNeedingAttention: connections.status === 'fulfilled'
          ? connections.value.filter(({ connection }) => {
            const status = connection.status?.toLowerCase();
            return status === 'error' || status === 'needs_reconnection';
          }).length
          : -1,
        devicesNeedingAttention: devices.status === 'fulfilled'
          ? devices.value.filter((device) => device.status !== 'active').length
          : -1,
      });
      if (devices.status === 'fulfilled') {
        setRecentDevices([...devices.value]
          .sort((left, right) => Date.parse(right.created_at) - Date.parse(left.created_at))
          .slice(0, 3));
      }
    });
    return () => {
      active = false;
    };
  }, []);

  if (!user) return null;
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
  const plan = billingSummary(user);

  return (
    <div className="account-page-content">
      <div className="page-heading">
        <p className="eyebrow">Account center</p>
        <h1>{name ? `Welcome, ${name}` : 'Welcome to Meridian'}</h1>
        <p>Manage the services and devices that keep your Meridian workspace moving.</p>
      </div>

      <section className="summary-grid" aria-label="Account summary">
        <SummaryCard
          label="Connected services"
          value={formatCount(counts?.connections)}
          detail={healthDetail(counts?.connectionsNeedingAttention, 'connection')}
          to="/account/connections"
        />
        <SummaryCard
          label="Active devices"
          value={formatCount(counts?.devices)}
          detail={healthDetail(counts?.devicesNeedingAttention, 'device')}
          to="/account/devices"
        />
        <SummaryCard
          label="Plan"
          value={plan.planLabel}
          detail={plan.through ? `Active through ${plan.through}` : 'View subscription options'}
          to="/account/billing"
        />
      </section>

      <section className="card">
        <div className="section-heading">
          <div>
            <h2>Recent security activity</h2>
            <p>New devices registered to this account.</p>
          </div>
          <Link className="btn btn-secondary btn-sm" to="/account/devices">
            Review devices
          </Link>
        </div>
        {recentDevices.length > 0 ? (
          <ul className="activity-feed" aria-label="Recent security activity">
            {recentDevices.map((device) => (
              <li key={device.id}>
                <div>
                  <strong>{device.name}</strong>
                  <span>Device registered</span>
                </div>
                <time dateTime={device.created_at}>{formatEventDate(device.created_at)}</time>
              </li>
            ))}
          </ul>
        ) : (
          <p className="empty">No device registration activity is available.</p>
        )}
      </section>

      <section className="card overview-profile">
        <div className="section-heading">
          <div>
            <h2>Your account</h2>
            <p>The identity Meridian uses for account notices and recovery.</p>
          </div>
          <Link className="btn btn-secondary btn-sm" to="/account/security">
            Manage security
          </Link>
        </div>
        <dl className="detail-list">
          <div><dt>Email</dt><dd>{user.email}</dd></div>
          <div><dt>Name</dt><dd>{name || <span className="empty">Not set</span>}</dd></div>
          <div><dt>Account status</dt><dd>{user.deleted_at ? 'Scheduled for deletion' : 'Active'}</dd></div>
        </dl>
      </section>

      <section className="card">
        <div className="section-heading">
          <div>
            <h2>Keep moving</h2>
            <p>Get the Mac app, find an answer, or reach a person.</p>
          </div>
        </div>
        <div className="form-actions">
          <a className="btn btn-primary" href="https://me.ridian.app/downloads/">
            Download Meridian
          </a>
          <a className="btn btn-secondary" href="https://me.ridian.app/help/">
            Help center
          </a>
          <a className="btn btn-secondary" href="mailto:support@ridian.app">
            Contact support
          </a>
        </div>
      </section>
    </div>
  );
}

function SummaryCard({ label, value, detail, to }: {
  label: string;
  value: string;
  detail: string;
  to: string;
}) {
  return (
    <Link to={to} className="summary-card">
      <span className="summary-label">{label}</span>
      <strong>{value}</strong>
      <span>{detail}</span>
    </Link>
  );
}

function formatCount(value: number | undefined): string {
  if (value === undefined) return '…';
  if (value < 0) return 'Unavailable';
  return String(value);
}

function healthDetail(value: number | undefined, noun: string): string {
  if (value === undefined) return 'Checking health…';
  if (value < 0) return 'Health unavailable';
  if (value === 0) return 'No issues reported';
  return `${value} ${noun}${value === 1 ? '' : 's'} need attention`;
}

function formatEventDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date unavailable';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
